import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  BeakerIcon,
  PresentationChartBarIcon,
  UserGroupIcon,
  ChatBubbleBottomCenterTextIcon,
  LightBulbIcon,
  AcademicCapIcon,
  UserIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Text } from "@core/text";
interface LearningMethodsStepProps {
  selectedMethods: string[] | undefined;
  onMethodsChange: (methods: string[]) => void;
  teachingStyle?: number;
  onTeachingStyleChange?: (value: number) => void;
}

const LearningMethodsStep: React.FC<LearningMethodsStepProps> = ({
  selectedMethods,
  onMethodsChange,
  teachingStyle = 50,
  onTeachingStyleChange,
}) => {
  const { t } = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  const methods = [
    {
      id: "lecture",
      icon: PresentationChartBarIcon,
      title: t("learningMethods.lecture.title"),
      description: t("learningMethods.lecture.description"),
    },
    {
      id: "collaborative",
      icon: UserGroupIcon,
      title: t("learningMethods.collaborative.title"),
      description: t("learningMethods.collaborative.description"),
    },
    {
      id: "groupDiscussion",
      icon: ChatBubbleBottomCenterTextIcon,
      title: t("learningMethods.groupDiscussion.title"),
      description: t("learningMethods.groupDiscussion.description"),
    },
    {
      id: "wholeClassDiscussion",
      icon: ChatBubbleBottomCenterTextIcon,
      title: t("learningMethods.wholeClassDiscussion.title"),
      description: t("learningMethods.wholeClassDiscussion.description"),
    },
    {
      id: "activeLearning",
      icon: LightBulbIcon,
      title: t("learningMethods.activeLearning.title"),
      description: t("learningMethods.activeLearning.description"),
    },
    {
      id: "experimental",
      icon: BeakerIcon,
      title: t("learningMethods.experimental.title"),
      description: t("learningMethods.experimental.description"),
    },
  ];

  const handleMethodToggle = (methodId: string) => {
    if (selectedMethods?.includes(methodId)) {
      onMethodsChange(selectedMethods?.filter((id) => id !== methodId));
    } else if ((selectedMethods?.length || 0) < 3) {
      onMethodsChange([...(selectedMethods || []), methodId]);
    }
  };

  const handleTeachingStyleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(e.target.value);
    if (onTeachingStyleChange) {
      onTeachingStyleChange(Math.round(value / 25) * 25);
    }
  };

  const getTeachingStyleLabel = (value: number) => {
    if (value <= 25) return t("learningMethods.teachingStyle.teacherCentered");
    if (value <= 50) return t("learningMethods.teachingStyle.balanced");
    return t("learningMethods.teachingStyle.studentCentered");
  };

  const getTeachingStyleColor = (value: number) => {
    if (value <= 25) return "!text-blue-600 dark:!text-blue-400";
    if (value <= 50) return "!text-emerald-600 dark:!text-emerald-400";
    return "!text-purple-600 dark:!text-purple-400";
  };

  const getSliderBackground = () => {
    return `linear-gradient(to right, 
      rgb(37, 99, 235) 0%, 
      rgb(37, 99, 235) ${teachingStyle <= 25 ? teachingStyle : 25}%, 
      rgb(5, 150, 105) ${teachingStyle > 25 && teachingStyle <= 50 ? teachingStyle : teachingStyle <= 25 ? 25 : 50}%, 
      rgb(147, 51, 234) ${teachingStyle > 50 ? teachingStyle : 50}%, 
      rgb(147, 51, 234) 100%)`;
  };

  const getTooltipText = (value: number) => {
    const tooltipValue = Math.round(value / 25) * 25;
    return t(`learningMethods.teachingStyle.tooltips.${tooltipValue}`);
  };

  const getTooltipPosition = (value: number) => {
    if (value <= 10) return "0%";
    if (value >= 90) return "90%";
    return `${value}%`;
  };

  return (
    <div className="space-y-8">
      {/* Learning Methods Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 min-h-[500px]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <AcademicCapIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <Text variant="h3">{t("learningMethods.title")}</Text>
          </div>
          <Text variant="span" size="sm" status="secondary">
            {t("learningMethods.selected", {
              count: selectedMethods?.length,
              max: 3,
            })}
          </Text>
        </div>

        {/* Learning Methods Helper */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {t("learningMethods.helper.title")}
              </h3>
              <ul className="mt-2 list-disc list-inside text-sm text-emerald-700 dark:text-emerald-300">
                <li>{t("learningMethods.helper.tip1")}</li>
                <li>{t("learningMethods.helper.tip2")}</li>
                <li>{t("learningMethods.helper.tip3")}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Method Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {methods.map((method) => {
            const isSelected = selectedMethods?.includes(method.id);
            const isDisabled =
              !isSelected && (selectedMethods?.length || 0) >= 3;

            return (
              <motion.button
                key={method.id}
                onClick={() => handleMethodToggle(method.id)}
                disabled={isDisabled}
                className={`relative p-4 rounded-lg border transition-all duration-200 text-left
                  ${
                    isSelected
                      ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 ring-2 ring-emerald-500 ring-opacity-50"
                      : isDisabled
                        ? "border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed"
                        : "border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-600 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20"
                  }
                `}
                whileHover={!isDisabled ? { scale: 1.02 } : {}}
                whileTap={!isDisabled ? { scale: 0.98 } : {}}
              >
                <div className="flex items-start space-x-4">
                  <method.icon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {method.title}
                    </h4>
                    <Text
                      variant="p"
                      className="mt-1"
                      status="secondary"
                      size="sm"
                    >
                      {method.description}
                    </Text>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Teaching Style Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 min-h-[300px]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <AcademicCapIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            <Text variant="h3">learningMethods.teachingStyle.title</Text>
          </div>
          <Text
            variant="span"
            size="sm"
            status="secondary"
            className={getTeachingStyleColor(teachingStyle)}
          >
            {getTeachingStyleLabel(teachingStyle)}
          </Text>
        </div>

        {/* Teaching Style Helper */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                {t("learningMethods.teachingStyle.title")}
              </h3>
              <Text variant="p" status="success" className="mt-2" size="sm">
                learningMethods.teachingStyle.description
              </Text>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <AcademicCapIcon className="w-4 h-4 text-blue-500" />
            <Text variant="span" size="sm" status="secondary">
              learningMethods.teachingStyle.teacher
            </Text>
          </div>
          <div className="relative flex-grow">
            <div className="flex justify-between mb-2">
              {[0, 25, 50, 75, 100].map((value) => (
                <Text key={value} variant="span" size="xs" status="secondary">
                  {value}
                </Text>
              ))}
            </div>
            <div className="relative">
              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={teachingStyle}
                onChange={handleTeachingStyleChange}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer relative z-10"
                style={{
                  background: getSliderBackground(),
                }}
                aria-label={t("learningMethods.teachingStyle.title")}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={teachingStyle}
                aria-valuetext={getTeachingStyleLabel(teachingStyle)}
              />
              <AnimatePresence>
                {showTooltip && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute -top-[135px] transform -translate-x-1/2 z-20 select-none pointer-events-none"
                    style={{ left: getTooltipPosition(teachingStyle) }}
                  >
                    <div className="relative bg-gray-900 rounded-xl shadow-xl overflow-hidden">
                      <div className="px-5 py-3 w-[300px] min-h-[60px] flex items-center justify-center">
                        <Text
                          variant="p"
                          className="text-center !text-white"
                          size="sm"
                        >
                          {getTooltipText(teachingStyle)}
                        </Text>
                      </div>
                      <div
                        className="absolute -bottom-[6px] left-1/2 transform -translate-x-1/2 w-3 h-3 bg-gray-900 rotate-45 shadow-lg"
                        style={{
                          boxShadow: "2px 2px 4px rgba(0,0,0,0.1)",
                        }}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex justify-between mt-2">
              <Text variant="span" size="xs" status="secondary">
                learningMethods.teachingStyle.teacherCentered
              </Text>
              <Text variant="span" size="xs" status="secondary">
                learningMethods.teachingStyle.balanced
              </Text>
              <Text variant="span" size="xs" status="secondary">
                learningMethods.teachingStyle.studentCentered
              </Text>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <UserIcon className="w-4 h-4 text-purple-500" />
            <Text variant="span" size="sm" status="secondary">
              learningMethods.teachingStyle.student
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningMethodsStep;
